<template>
	<v-container class="marketing-card-component">
		<p v-if="marketingCard.isPreview">Preview: "{{ marketingCard.name }}"</p>
		<v-layout row wrap>
			<template v-if="!showSingleMarketingCard">
				<v-flex
					v-for="(topTemplate, topIndex) in cardGrid"
					:key="topIndex"
					class="row-wrapper"
					xs12 sm12 md12 lg12 xl12
					:style="{
						'justify-content': topTemplate.rows.length > 1 ? 'space-between' : 'space-around'
					}"
				>
					<template v-if="isMobileChannel || isPendingOrderPage || isFrontPageMarketingCard">
						<template v-if="topTemplate.showAsCarousel">
							<div class="marketing-card-carousel">
								<div class="marketing-card-carousel-header">
									<div class="marketing-card-carousel-title">{{ $t(`targetGroups.${marketingCard.id}.row.${topIndex}.carouselTitle`) }}</div>
									<div class="marketing-card-carousel-length">{{ visibleText(topIndex) }}</div>
								</div>
								<carousel
									:ref="`carousel-${topIndex}`"
									:autoplay="false"
									:per-page="1.2"
									:mouseDrag="true"
									:scrollPerPage="false"
									:centerMode="true"
									:paginationEnabled="false"
									:navigationEnabled="false"
									:navigationPrevLabel="''"
									:navigationNextLabel="''"
									@pageChange="onPageChange(topIndex, $event)"
								>
									<slide v-for="(template, index) in topTemplate.rows" :key="index">
										<component
											:is="generateComponentName(topIndex, index)"
											:key="generateComponentName(topIndex, index)"
											:style="componentClasses(topTemplate.rows.length, template.aspectRatio, generateComponentName(topIndex, index), topTemplate.showAsCarousel)"
											:experimentId="`${template.experimentId}`"
											:title="(template.locales && template.locales[$store.getters.lastLocale].title) || ''"
											v-bind="extraCardProps(template)"
											class="flex component-class"
											:class="{ 'cursor-pointer': isThereOneProductAvailable(template.productIds || []) }"
										/>
									</slide>
								</carousel>
							</div>
						</template>
						<template v-else>
							<component
								v-for="(template, index) in topTemplate.rows"
								:is="generateComponentName(topIndex, index)"
								:key="generateComponentName(topIndex, index)"
								:style="componentClasses(topTemplate.rows.length, template.aspectRatio, generateComponentName(topIndex, index), topTemplate.showAsCarousel)"
								:experimentId="`${template.experimentId}`"
								:title="(template.locales && template.locales[$store.getters.lastLocale].title) || ''"
								v-bind="extraCardProps(template)"
								class="flex component-class"
								:class="{ 'cursor-pointer': isThereOneProductAvailable(template.productIds || []) }"
							/>
						</template>
					</template>
					<v-carousel v-else
						id="banner-carousel"
						class="carousel-styled"
						height="auto"
						:hide-controls="hideControls"
						:hide-delimiters="true"
						:interval="tenantSettings.timers.marketingCardsSwitchInterval || 5000"
					>
						<v-carousel-item
							v-for="(template, index) in topTemplate.rows"
							:key="generateComponentName(topIndex, index)"
							reverse-transition="fade-transition"
							transition="fade-transition"
						>
							<component
							:is="generateComponentName(topIndex, index)"
							class="flex"
							:class="{ 'cursor-pointer': isThereOneProductAvailable(template.productIds || []) }"
							/>
						</v-carousel-item>
					</v-carousel>
				</v-flex>
			</template>
			<template v-else >
				<div class="w-100">
					<component
						:is="generateComponentName(0, currentMarketingCardIndex)"
						:key="generateComponentName(0, currentMarketingCardIndex)"
						class="single-marketing-card"
				/>
				</div>
			</template>
		</v-layout>
		<upsell-rules
			v-show="displayUpsellModal"
			:upsellFlow="upsellFlows.RCM_MARKETING_CARD"
			:displayUpsellModal="displayUpsellModal"
			:providedProductsMap="recommendedProductsMap"
			@close="() => displayUpsellModal = false"
			@closeUpsellModal="() => displayUpsellModal = false"
		/>
	</v-container>
</template>

<script>
import Vue from "vue";
import {mapGetters} from "vuex";
import {
	canBePickedUp,
	getIdAndNameFromCategory,
	imageLoaderNew
} from "../../../libraries/utils";
import appConfig, {channelEnum, frequentlyUsedChannelEnum} from "../../../appconfig";
import ProductSelection from "../../../domains2/orders/ProductSelection";
import * as actions from "../../../store/mutation-types";
import OrderItemCommonMixin from "../../../mixins/OrderItemCommon.mixin";
import {isMenu} from "../../../domains2/libraries/Utils";
import productCatalogMenuMixin from "../../../mixins/productCatalogMenu.mixin";
import {generateDifferentiatedGroups} from "../../../helper/ProductSelectionHelper";
import goToMixin from "../../../mixins/goTo.mixin";
import UpsellRules from "../../ShopComponents/gt3/ShoppingCart/UpsellRules-gt3.vue";
import {UPSELL_FLOWS} from "../../../libraries/globalTemplateSpecific/bm100/utils";
import {goToConfigurationStep} from "../../../helper/ProductConfigurationRedirectionHelper";
import HelloRetail from "../../../integrations/recommendations/helloRetail/HelloRetail";
import {updateImagePaths} from "../../../helper/MarketingCardHelper";
import SimpleSkeletonCard from "../SimpleSkeletonCard.vue";
import {Carousel, Slide} from "vue-carousel/src";
import {isProductCatalogDataAvailable} from "../../../store/ProductCatalogNonReactiveStorage";
import LoggerProvider from "../../../providers/LoggerProvider";

// TODO: we need first 4 items as well to prevent breaking old apps, up until next forced update
const DYNAMIC_COMPONENT_KEYS = {
	experimentId: "experimentId",
	isVoucherCarousel: "isVoucherCarousel",
	isCatalogCarousel: "isCatalogCarousel",
	isReorderHighlight: "isReorderHighlight",
	PRODUCT_CAROUSEL: "PRODUCT_CAROUSEL",
	VOUCHER_CAROUSEL: "VOUCHER_CAROUSEL",
	CATALOG_CAROUSEL: "CATALOG_CAROUSEL",
	REORDER_ELEMENT: "REORDER_ELEMENT"
};

const DYNAMIC_COMPONENT_MAP = {
	[DYNAMIC_COMPONENT_KEYS.experimentId]: "ProductCarousel/ProductCarousel",
	[DYNAMIC_COMPONENT_KEYS.isVoucherCarousel]: "VoucherCarousel/VoucherCarousel",
	[DYNAMIC_COMPONENT_KEYS.isCatalogCarousel]: "CatalogCarousel/CatalogCarousel",
	[DYNAMIC_COMPONENT_KEYS.isReorderHighlight]: "ReorderHighlight/ReorderHighlight",
	[DYNAMIC_COMPONENT_KEYS.PRODUCT_CAROUSEL]: "ProductCarousel/ProductCarousel",
	[DYNAMIC_COMPONENT_KEYS.VOUCHER_CAROUSEL]: "VoucherCarousel/VoucherCarousel",
	[DYNAMIC_COMPONENT_KEYS.CATALOG_CAROUSEL]: "CatalogCarousel/CatalogCarousel",
	[DYNAMIC_COMPONENT_KEYS.REORDER_ELEMENT]: "ReorderHighlight/ReorderHighlight"
};

export default {
	name: "MarketingCards",
	components: {
		UpsellRules,
		Carousel,
		Slide
	},
	props: {
		marketingCard: {
			type: Object,
			required: false
		},
		receiptLines: {
			type: Array,
			required: false
		},
		orderLines: {
			type: Array,
			required: false
		},
		showSubscriptionPlans: {
			type: Function,
			required: false
		},
		currentProductId: {
			type: String,
			required: false
		}
	},
	mixins: [OrderItemCommonMixin, productCatalogMenuMixin, goToMixin],
	data() {
		return {
			productSelection: null,
			receiptLine: null,
			orderLine: null,
			carouselHovered: false,
			showSingleMarketingCard: false,
			currentMarketingCardIndex: 0,
			displayUpsellModal: false,
			recommendedProductsMap: {},
			addReorderLoading: false,
			reorderIndex: null,
			visibleIndexes: {}
		};
	},
	watch: {
		currentProductId() {
			this.showSingleMarketingCard = !!(this.currentProductId
				&& this.marketingCard
				&& (this.marketingCard.options.products || []).includes(this.currentProductId));
			if(this.showSingleMarketingCard) {
				this.refreshHTML();
			}
		},
		configurationStep(newValue, oldVal) {
			if(
				!(this.marketingCard
					&& this.marketingCard.options
					&& this.marketingCard.options.grid.length
					&& this.marketingCard.options.grid[0].rows)
			) {
				return;
			}
			if(oldVal < newValue && newValue < this.marketingCard.options.grid[0].rows.length) {
				this.currentMarketingCardIndex++;
			} else if(oldVal > newValue && oldVal < this.marketingCard.options.grid[0].rows.length) {
				this.currentMarketingCardIndex--;
			}
		},
		marketingCard() {
			if(this.isDesktopBrowserChannel || this.isKioskChannel) {
				this.currentMarketingCardIndex = 0;
				setTimeout(() => {
					this.addEvents();
				});
			}
			this.renderHTML();
		},
		// This watcher is commented out intentionally
		// it will be more looked into when the time allows it
		// cardGrid() {
		// 	this.renderHTML();
		// },
		"$store.state.localization.persistedData.lastLocaleUsed": {
			handler: function () {
				this.renderHTML();
			}
		}
	},
	created() {
		this.renderHTML();
	},
	mounted() {
		window.addEventListener("resize", this.refreshHTML);
		this.addEvents();
	},
	beforeDestroy() {
		window.removeEventListener("resize", this.handleScroll);
	},
	computed: {
		visibleText() {
			return (topIndex) => {
				const rows = this.cardGrid[topIndex].rows.length;
				const current = (this.visibleIndexes[topIndex] || 0) + 1;
				return `${current} ${this.$t("common.of")} ${rows}`;
			};
		},
		upsellFlows() {
			return UPSELL_FLOWS;
		},
		...mapGetters(["featureToggles", "tenantSettings", "configurationStep", "rcmMarketingCard"]),
		isDesktopBrowserChannel() {
			return appConfig.channelAlias() === channelEnum.desktopBrowser;
		},
		isKioskChannel() {
			return appConfig.channelAlias() === channelEnum.kiosk;
		},
		isMobileChannel() {
			return [channelEnum.mobileApp, channelEnum.mobileBrowser].includes(appConfig.channelAlias());
		},
		cardGrid() {
			try {
				let productIdMatch = null;
				const isRowMarketingCardGroup = (row) => "marketingCards" in row;

				const grid = this.marketingCard.options.grid.filter(card => card.rows.length>0);
				outerLoop: for (const gridItem of grid) {
					for (const row of gridItem.rows) {
						if (isRowMarketingCardGroup(row)) {
							for (const productId of this.rcmMarketingCard.productIds) {
								for (const mcItem of row.marketingCards) {
									if ((mcItem.productIds || []).includes(productId)) {
										const product = this.getProduct(productId);
										if (product) {
											productIdMatch = productId;
											break outerLoop;
										}
									}
								}
							}
						}
					}
				}

				const validMarketingCard = (row) => {
					if (!isProductCatalogDataAvailable()) {
						return true;
					}
					// if there are productIds attached, check if any of the product ids is in catalog, if yes, then marketing card is valid
					// if there are no productIds attached, marketing card is considered valid;
					const availableProductIds = (row.productIds || []).filter(id => this.getProduct(id));
					return availableProductIds.length ? availableProductIds.find(x => this.getProduct(x)) : true;
				};
				return grid.map(item => {
					const rows = item.rows.reduce((acc, row) => {
						// we should show bigger image in case MCs are shown as carousel or there is a single MC in row
						const isSingleColumn = item.rows.length <= 1 || item.showAsCarousel;
						row = updateImagePaths(row, this.isMobileChannel, isSingleColumn);
						if (productIdMatch) {
							if (isRowMarketingCardGroup(row)) {
								if (this.rcmMarketingCard.enable && productIdMatch) {
									const correspondingMC = row.marketingCards.filter(x=> (x.productIds || []).filter(id => this.getProduct(id)).includes(productIdMatch));
									acc.push(...correspondingMC);
								}
							}
						} else {
							if (!(isRowMarketingCardGroup(row)) && validMarketingCard(row)) {
								acc.push(row);
							}
						}
						return acc;
					}, []);
					return {...item, rows};
				});
			} catch (e) {
				LoggerProvider.logEvent({
					message: "MarketingCards: cardGrid computation failed",
					eventName: "MarketingCardsGridFailure",
					eventCategory: "ui",
					eventDomain: "marketing",
					data: {
						cardId: this.marketingCard && this.marketingCard.id,
						hasOptions: !!(this.marketingCard && this.marketingCard.options),
						rcmEnabled: this.rcmMarketingCard && this.rcmMarketingCard.enable,
						error: e.message
					}
				});
				return [];
			}
		},
		isSingleCard() {
			return this.cardGrid.map(topTemplate => topTemplate.rows.map(template=>template)).flat().length === 1;
		},
		/**
		 * For both kiosk and desktop channels hide controls when there is only 1 marketing card
		 * For desktop: hide controls when carousel is not hovered, when carousel is hovered show controls
		 * For kiosk: do not hide controls
		 */
		hideControls() {
			if (this.isSingleCard) {
				return true;
			}
			if (this.isDesktopBrowserChannel) {
				return !this.carouselHovered;
			}
			return false;
		},
		isPendingOrderPage() {
			return this.$route.name === "PendingOrder";
		},
		isFrontPageMarketingCard() {
			return this.isKioskChannel && this.featureToggles.enableFrontpageMarketingCards;
		},
		isReorderRedesigned() {
			return (appConfig.channelAlias() === frequentlyUsedChannelEnum.mobileBrowser || appConfig.mobile.isMobileApp()) && this.featureToggles.reorderRedesigned;
		}
	},
	methods: {
		onPageChange(topIndex, page) {
			this.$set(this.visibleIndexes, topIndex, Math.round(page));
		},
		onMouseLeave() {
			this.carouselHovered = false;
		},
		onMouseEnter() {
			this.carouselHovered = true;
		},
		/**
		 * assign events in case of desktop, and when there is more than 1 card to display. This is to mimic hover behavior and show carousel arrows only on hover, its requirement
		 */
		addEvents() {
			if (!this.isDesktopBrowserChannel || this.isSingleCard) {
				return;
			}
			const carousel = document.getElementById("banner-carousel");
			if(!carousel) {
				return;
			}
			carousel.addEventListener("mouseleave", this.onMouseLeave);
			carousel.addEventListener("mouseenter", this.onMouseEnter);
		},
		refreshHTML() {
			return this.$nextTick(this.$forceUpdate);
		},
		generateComponentName(topIndex, index) {
			return `marketing_${topIndex}_${index}`;
		},
		renderHTML() {
			const methods = {
				goTo: this.goTo,
				addToBasket: this.addToBasket,
				backImg: this.backImg
			};
			if (this.showSubscriptionPlans) {
				methods.showSubscriptionPlans = this.showSubscriptionPlans;
			}
			this.cardGrid
				.forEach((marketingCardColumn, topIndex) => marketingCardColumn.rows
					.forEach((marketingCard, index) => {
						try {
							const languageCode = this.$store.state.localization.persistedData.lastLocaleUsed;
							let template = ((marketingCard.locales || {})[languageCode] || {}).template || marketingCard.template;
							const match = template.match(/"goTo\('(.*)'\)">/);

							if (match && match[1]) {
								const goToSection = match[1];
								const uriItems = decodeURIComponent(goToSection).split("?");
								const params = new URLSearchParams(uriItems[1]);
								const productIds = params.get("productIds");
								if (productIds && productIds.length) {
									const ids = productIds.split(",");
									const updateTemplate = (productId) => {
										const item = this.getProduct(productId);
										if (item) {
											params.delete("productIds");
											params.set("productId", productId);
											const replacement = uriItems[0] + "?" + params.toString();
											template = template.replace(/"goTo\('(.*)'\)">/, `"goTo('${replacement}', '${productIds}')">`);
											return true;
										}
									};
									if (this.rcmMarketingCard.enable) {
										for (const rcmProductId of this.rcmMarketingCard.productIds) {
											if (!ids.includes(rcmProductId)) {
												continue;
											}
											if (updateTemplate(rcmProductId)) {
												break;
											}
										}
									} else {
										for (const id of ids) {
											if (updateTemplate(id)) {
												break;
											}
										}
									}
								}
							}

							// if there is no product catalog available
							// marketing cards with experimentId will be filtered out at a higher level
							return Vue.component(this.generateComponentName(topIndex, index),
								(() => {
									const file = this.resolveMarketingCardDynamicComponent(marketingCard);
									return file
										? () => ({
											component: import(`./${file}.vue`),
											loading: SimpleSkeletonCard,
											delay: 0
										})
										: {template, methods};
								})()
							);
						} catch (e) {
							LoggerProvider.logEvent({
								message: "MarketingCards: renderHTML failed for card",
								eventName: "MarketingCardsRenderFailure",
								eventCategory: "ui",
								eventDomain: "marketing",
								data: {
									cardId: this.marketingCard && this.marketingCard.id,
									languageCode: this.$store.state.localization.persistedData.lastLocaleUsed,
									topIndex,
									index,
									error: e.message
								}
							});
						}
					}));
			this.refreshHTML();
		},
		resolveMarketingCardDynamicComponent(marketingCard) {
			const key = Object.keys(DYNAMIC_COMPONENT_KEYS)
				.filter(componentKey => {
					if (componentKey === DYNAMIC_COMPONENT_KEYS.isReorderHighlight || componentKey === DYNAMIC_COMPONENT_KEYS.REORDER_ELEMENT) {
						return this.isReorderRedesigned && this.globalSelectedShop;
					}
					return true;
				})
				.find((componentKey) => {
					if(marketingCard.marketingCardType && marketingCard.marketingCardType.length) {
						return marketingCard.marketingCardType === componentKey;
					}
					return marketingCard[componentKey];
				});
			return key ? DYNAMIC_COMPONENT_MAP[key] : null;
		},
		backImg(imageName) {
			return imageLoaderNew({folderName: "cards", imageLink: imageName, type: "dynamic", allowOriginalFormat: true});
		},
		isThereOneProductAvailable(productIds) {
			for (const productId of productIds) {
				const item = this.getProduct(productId);
				if (item) {
					return true;
				}
			}
		},
		getProduct(productId) {
			// if there is an invalid id or deactivated product, we don't want to crush the app
			try {
				const categoriesOfProduct = this.productCatalog.getAllCategoriesOfProduct(productId);
				const isProductCategoriesContainSelectedShop = categoriesOfProduct.some(
					(category) => Array.isArray(category.isSpecificForShops)
						&& category.isSpecificForShops.includes(this.globalSelectedShop.id)
				);
				const isProductSupportedByActiveChannel = !categoriesOfProduct.length
					|| categoriesOfProduct.filter(category => !category.channels || category.channels.includes(appConfig.channelAlias())).length;
				const product = this.productCatalog.getProduct(productId);
				const isProductAvailable = canBePickedUp(
					product,
					this.productCatalog,
					this.cachedPickupRules,
					this.globalSelectedShop.id
				);
				// if the product is not available in the selected restaurant it can't be added to the cart
				return isProductAvailable && isProductCategoriesContainSelectedShop && isProductSupportedByActiveChannel ? product : false;
			} catch (e) {
				return false;
			}
		},
		async addToBasket(productId) {
			const product = this.getProduct(productId);
			if(!product) {
				return false;
			}
			const isMenuProduct = isMenu(this.productCatalog, productId);
			if(isMenuProduct) {
				generateDifferentiatedGroups(product);
				const item = {product};
				const newProductSelection = this.createCompositeSelectedProducts(item);
				item.productSelections = this.findOrCreateProductSelection(item.product.id)
					.concat({
						...newProductSelection,
						category: getIdAndNameFromCategory(this.category)
					});
				return goToConfigurationStep(this, {item});
			}

			if (this.selections.findIndex(item=>item.productId === product.id) >= 0) {
				this.productSelection = this.selections.find(item=>item.productId === product.id);
				this.orderLine = this.orderLines.find(item => item.productId === product.id);
				this.receiptLine = this.receiptLines.find(item => item.productId === product.id);
				this.product = this.productCatalog.getProduct(product.id);
				await this.changeQuantity(this.productSelection.quantity + 1, true);
				return;
			}
			const psOptions = {
				expiration: {
					tenantSettings: this.tenantSettings.productSelectionExpiration,
					tenantConfig: appConfig.tenantConfig.productSelectionExpiration
				}
			};
			const productSelection = ProductSelection.createFromProduct(product, psOptions);
			productSelection.quantity = productSelection.quantity || 1;
			this.$store.dispatch(actions.ADD_PRODUCT_SELECTION_TO_CART, productSelection);
			HelloRetail.cartTracking(this);
		},
		componentClasses(gridItemLength, aspectRatio, componentName, showAsCarousel){
			const stylesCreated = [];
			if (!showAsCarousel && gridItemLength > 1) {
				const width = 100 / gridItemLength - 2;
				stylesCreated.push(`max-width: ${width}%`);
				stylesCreated.push(`min-width: ${width}%`);
			}

			const [ widthRatio, heightRatio ] = (aspectRatio || "")
				.split("T")
				.map(Number);
			if (widthRatio && heightRatio) {
				const offsetWidth = this.$children
					.filter((comp) => comp.$options.name === componentName)
					.map((comp) => comp.$el.offsetWidth)
					.find(Boolean);

				const newHeight = (heightRatio / widthRatio) * offsetWidth;
				stylesCreated.push(
					`max-height: ${newHeight}px`,
					`min-height: ${newHeight}px`
				);
			}
			return stylesCreated.join("; ");
		},
		handleReorderLoadingChange(isLoading, index) {
			this.addReorderLoading = isLoading;
			this.reorderIndex = index;
		},
		extraCardProps(card) {
			if (card.isReorderHighlight) {
				return {
					addReorderLoading: this.addReorderLoading,
					reorderIndex: this.reorderIndex,
					handleReorderLoadingChange: this.handleReorderLoadingChange
				};
			}
			return {};
		}
	}
};
</script>

<style lang="scss">
.cursor-pointer {
  cursor: pointer;
}
.marketing-card-component {
	color: white;
	width: 100%;
	background: #000000;
	display: flex;
	flex-direction: column;
	padding: 0 16px;

	.row-wrapper{
		flex-flow: row wrap;
		width: 100%;
		display: flex;
		margin-top: 16px;
		height: fit-content;
		align-items: center;

		.component-class{
			background-color: #000000;
			min-height: 100%;
			overflow: hidden;
			min-width: 48%;
		}

	}
  #banner-carousel {
    .v-responsive {
      img {
        width: auto;
      }
    }
  }
	.carousel-styled {
		.v-btn .v-btn__content .v-icon  {
      color: white;
    }
	}
}
.w-100 {
	width: 100%;
}
.single-marketing-card {
	display: flex;
	position: relative;
	height: calc(100vh - 204px);
	align-items: center;
	justify-content: center;
	width: 100%;
}

.marketing-card-carousel {
	display: flex;
	flex-direction: column;
	width: 100%;
	.marketing-card-carousel-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 16px;
	}
	.marketing-card-carousel-title {
		font-size: 18px;
		font-weight: 700;
		line-height: 1;
		text-align: left;
	}
	.marketing-card-carousel-length {
		font-size: 16px;
		line-height: 1;
		min-width: 70px;
		text-align: right;
	}
	.VueCarousel-slide {
		flex-grow: 1;
	}
	.VueCarousel-slide:not(:last-child) {
		padding-right: 10px;
	}
}
</style>
